import { useEpvFamilies } from '@/hooks/api';
import { formatNum, getEpvFamilyById, isInRange } from '@/utils/utils';
import { ChartDataset, ScriptableContext } from 'chart.js';
import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { generateColorPalette } from '../scenario/capacity/CapacityChart';

interface MatchingDeltaProps {
	data: TMatchingData | undefined;
	yearRange: [number, number];
	processStep: TValue;
	by: TValue;
	displays: TValue[];
	selectedEpvFamilies: TValue[];
	selectedProcessStep: TProcessStep | undefined;
}

type TChartDataSet = ChartDataset<'bar' | 'line', number[]>;

const palette = generateColorPalette();

const MatchingDelta: React.FC<MatchingDeltaProps> = ({
	data,
	processStep,
	yearRange,
	by,
	displays,
	selectedEpvFamilies,
	selectedProcessStep,
}) => {
	const isGlobalRegions = by === 'global regions';
	const isProductionRegion = by === 'production region';
	const isPlant = by === 'plant';

	const { data: epvFamilies } = useEpvFamilies();

	const getDemandDeltas = (display: string | null) => {
		if (!data) return {};

		const deltasByYear: Record<number, Record<number, number>> = {};
		for (const d of data.demand_fulfilled) {
			const {
				year,
				epv_family_lvl2_id,
				global_region,
				production_region,
				matching,
			} = d;
			if (!isInRange(year, yearRange)) continue;
			const fullfilledKey = `${processStep}_fulfilled`;
			const fullfilled = d[fullfilledKey as keyof typeof d] as number;
			const demandKey = `${processStep}_demand`;
			const demand = d[demandKey as keyof typeof d] as number;
			if (display) {
				if (isGlobalRegions && display !== global_region) continue;
				if (isProductionRegion && display !== production_region) continue;
				if (isPlant && display !== matching) continue;
			}
			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!selectedEpvFamilies.includes(epv_family_lvl2_id)
			)
				continue;

			if (deltasByYear[year]) {
				if (deltasByYear[year][epv_family_lvl2_id]) {
					deltasByYear[year][epv_family_lvl2_id] += fullfilled - demand;
				} else {
					deltasByYear[year][epv_family_lvl2_id] = fullfilled - demand;
				}
			} else {
				deltasByYear[year] = { [epv_family_lvl2_id]: fullfilled - demand };
			}
		}

		return deltasByYear;
	};

	const getCapaDeltas = (display: string | null) => {
		if (!data) return {};

		const deltasByYear: Record<number, number> = {};
		for (const c of data.capacity_used) {
			const {
				year,
				capacity,
				capacity_used,
				capacity_type,
				global_region,
				production_region,
				matching,
				epv_family_lvl2_ids,
			} = c;
			if (!isInRange(year, yearRange)) continue;
			if (display) {
				if (isGlobalRegions && display !== global_region) continue;
				if (isProductionRegion && display !== production_region) continue;
				if (isPlant && display !== matching) continue;
			}

			if (
				selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered &&
				selectedEpvFamilies.length > 0 &&
				!epv_family_lvl2_ids.every((id) => selectedEpvFamilies.includes(id))
			)
				continue;

			if (capacity_type === processStep) {
				if (deltasByYear[year]) {
					deltasByYear[year] += capacity - capacity_used;
				} else {
					deltasByYear[year] = capacity - capacity_used;
				}
			}
		}

		return deltasByYear;
	};

	const convertDemandDeltasyYearToChartDatasets = (
		data: Record<number, Record<number, number>>
	) => {
		const datasets: TChartDataSet[] = [];
		const years = Object.keys(data);
		if (!years.length) return [];

		const epvs = Object.keys(data[years[0] as any]);
		for (const epvFamily of epvs) {
			const f = getEpvFamilyById(epvFamilies, +epvFamily);
			const backgroundColor = palette[epvs.indexOf(epvFamily)];
			const dataset: TChartDataSet = {
				type: 'bar',
				backgroundColor,
				label: f?.epv_family_lvl2,
				data: [],
			};

			for (const year of years) {
				dataset.data.push(data[+year][epvFamily as any]);
			}

			datasets.push(dataset);
		}

		return datasets;
	};

	const labels = useMemo(
		() =>
			Array.from(
				{ length: yearRange[1] - yearRange[0] + 1 },
				(_, i) => i + yearRange[0]
			),
		[data]
	);

	const min = displays.reduce((acc, display) => {
		const capa = Object.values(getCapaDeltas(display as string));
		const demand = Object.values(getDemandDeltas(display as string)).flatMap(
			(d) => Object.values(d).reduce((acc, v) => acc + v, 0)
		);
		const minCapa = Math.min(...capa);
		const minDemand = Math.min(...demand);
		const min = Math.min(minCapa, minDemand);
		if (min < (acc as number)) return min;

		return acc;
	}, 0);

	const max = displays.reduce((acc, display) => {
		const capa = Object.values(getCapaDeltas(display as string));
		const demand = Object.values(getDemandDeltas(display as string)).flatMap(
			(d) => Object.values(d).reduce((acc, v) => acc + v, 0)
		);
		const minCapa = Math.max(...capa);
		const minDemand = Math.max(...demand);
		const max = Math.max(minCapa, minDemand);
		if (max > (acc as number)) return max;

		return acc;
	}, 0);

	if (!data || !processStep) return null;

	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(2, 1fr)',
			}}>
			{by === 'group' && (
				<Chart
					style={{
						maxHeight: '500px',
					}}
					type='bar'
					data={{
						labels,
						datasets: [
							{
								type: 'bar',
								data: Object.values(getCapaDeltas(null)),
								label: 'Remaining capacity',
								backgroundColor: (ctx: ScriptableContext<'bar'>) => {
									const value = ctx.dataset.data[ctx.dataIndex] as number;
									return value < 0
										? '#bd2015'
										: value >= 0.5
										? '#009a52'
										: '#fee2ce';
								},
							},
							...convertDemandDeltasyYearToChartDatasets(getDemandDeltas(null)),
						],
					}}
					options={{
						scales: {
							y: {
								stacked: true,
							},
							x: {
								stacked: true,
							},
						},
						plugins: {
							legend: {
								display: true,
							},
							title: {
								display: true,
							},
							tooltip: {
								callbacks: {
									label: (ctx) => {
										const index = ctx.dataIndex;
										const value = ctx.dataset.data[index] as number;
										const label = ctx.dataset.label;
										return label + ' ' + formatNum({ value });
									},
								},
							},
						},
					}}
				/>
			)}
			{displays.map((display) => (
				<Chart
					key={display as string}
					style={{
						maxHeight: '500px',
						maxWidth: '45vw',
					}}
					type='bar'
					data={{
						labels,
						datasets: [
							{
								type: 'bar',
								data: Object.values(getCapaDeltas(display as string)),
								label: 'Remaining capacity',
								backgroundColor: (ctx: ScriptableContext<'bar'>) => {
									const value = ctx.dataset.data[ctx.dataIndex] as number;
									return value < 0
										? '#bd2015'
										: value >= 0.5
										? '#009a52'
										: '#fee2ce';
								},
							},
							...convertDemandDeltasyYearToChartDatasets(
								getDemandDeltas(display as string)
							),
						],
					}}
					options={{
						scales: {
							y: {
								stacked: true,
								min: (min as number) + (min as number) / 5,
								max: (max as number) + (max as number) / 5,
							},
							x: {
								stacked: true,
							},
						},
						plugins: {
							legend: {
								display: true,
							},
							tooltip: {
								callbacks: {
									label: (ctx) => {
										const index = ctx.dataIndex;
										const value = ctx.dataset.data[index] as number;
										const label = ctx.dataset.label;
										return label + ' ' + formatNum({ value });
									},
								},
							},

							title: {
								display: true,
								text:
									(isGlobalRegions
										? 'Global Region'
										: isProductionRegion
										? 'Production Region'
										: 'Plant') + ` ${display}`,
							},
						},
					}}
				/>
			))}
		</div>
	);
};

export default MatchingDelta;
